import React, { useCallback, useContext, useMemo } from 'react'
import { string, bool, object, node } from 'prop-types'
import { connect } from 'react-redux'
import { css } from '@emotion/react'

import Breadcrumbs from '../Breadcrumbs'
import { isAppInstalled } from '@/store/selectors'
import { DraftModeContext } from '@/context/draftMode'
import mediaQueries from '@/layout/media-queries'
import { isAgentApp } from '@/utils'
import { isAllAppsCategory } from '@/utils/products'

const breadcrumbsWrapperCss = (theme) => css`
  height: 44px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${theme.layout.breadcrumbs.borderColor};
  background: ${theme.colors.white};
  padding: 0px 16px;

  ${mediaQueries.mediumScreen(100)} {
    border-bottom: none;
    padding: 0;
    width: auto;
  }
`

const ProductBreadcrumbs = ({
  id,
  name,
  category,
  isService,
  actions,
  className,
}) => {
  const draftMode = useContext(DraftModeContext)

  const buildBreadcrumbs = useCallback(() => {
    const items = []
    const isAllApps = isAllAppsCategory(category)

    if (isAgentApp) {
      items.push({ path: '/apps/', title: 'Explore apps' })
    } else if (isService) {
      items.push({ path: '/services/', title: 'Services' })
    } else {
      items.push({ path: '/apps/', title: 'Apps' })
    }

    if (draftMode) {
      const categoryConsoleLink = `${process.env.GATSBY_CONSOLE_URL}/apps/${id}/marketplace/listing`

      if (category?.slug) {
        items.push({
          title: (
            <span css={(theme) => `color: ${theme.colors.primaryAlpha(0.4)};`}>
              {category.name} (
              <a
                href={categoryConsoleLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Change Category
              </a>
              )
            </span>
          ),
        })
      } else {
        items.push({
          title: (
            <span css={(theme) => `color: ${theme.colors.primaryAlpha(0.4)};`}>
              (
              <a
                href={categoryConsoleLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Select Category
              </a>
              )
            </span>
          ),
        })
      }
    }

    if (category?.slug && !isAllApps) {
      const slug = `${category.slug}/`
      items.push({ path: `/apps/${slug}`, title: category.name })
    }

    if (name) {
      items.push({ path: null, title: name })
    }

    return items
  }, [category?.name, category?.slug, draftMode, id, isService, name])

  const breadcrumbsItems = useMemo(() => buildBreadcrumbs(), [buildBreadcrumbs])

  return (
    <div css={breadcrumbsWrapperCss} className={className}>
      <Breadcrumbs items={breadcrumbsItems} />
      {actions}
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  return {
    isInstalled: isAppInstalled(state, ownProps.id),
    isService:
      ownProps?.customProps?.isService === 'true' || ownProps.isServiceView,
  }
}

ProductBreadcrumbs.propTypes = {
  id: string,
  name: string,
  category: object,
  isService: bool,
  isServiceView: bool,
  actions: node,
  className: string,
}

export default connect(mapStateToProps)(ProductBreadcrumbs)
